import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, Search, ArrowRight, Download, Loader2, Users } from 'lucide-react';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { getAuditLog, clearAuditLog, fetchAuditLogFromApi, fetchAllAuditLogEntriesFromApi, shouldUseLiveAuditApi } from '@/lib/audit-log-storage';
import type { AuditCategory, AuditEntry } from '@/lib/audit-log-storage';
import { exportAuditLogToExcel } from '@/lib/export-audit-log-excel';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type CategoryFilter = AuditCategory | 'all';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'Settings', label: 'Settings' },
  { value: 'Project', label: 'Project' },
  { value: 'Resource', label: 'Resource' },
  { value: 'Budget', label: 'Budget' },
  { value: 'Client', label: 'Client' },
];

const CATEGORY_BADGE_VARIANT: Record<AuditCategory, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  Settings: 'default',
  Project: 'secondary',
  Resource: 'outline',
  Budget: 'destructive',
  Client: 'outline',
};

const CATEGORY_ICON: Partial<Record<AuditCategory, React.ReactNode>> = {
  Client: <Users className="h-3 w-3 mr-1 inline-block" />,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AuditLogPage() {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Live mode state
  const isLive = shouldUseLiveAuditApi();
  const [liveEntries, setLiveEntries] = useState<AuditEntry[]>([]);
  const [liveTotal, setLiveTotal] = useState(0);
  const [loading, setLoading] = useState(isLive);

  // Handle mode transitions — reset state when toggling demo ↔ live
  const prevIsLiveRef = useRef(isLive);
  if (prevIsLiveRef.current !== isLive) {
    prevIsLiveRef.current = isLive;
    if (isLive) {
      setLoading(true);
    } else {
      setLiveEntries([]);
      setLiveTotal(0);
      setLoading(false);
    }
  }

  // Fetch from API when in live mode
  const fetchLiveData = useCallback(async () => {
    if (!isLive) return;
    setLoading(true);
    try {
      const result = await fetchAuditLogFromApi({
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        search: search.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setLiveEntries(result.entries);
      setLiveTotal(result.total);
    } catch (error) {
      console.error('Error fetching audit log:', error);
      setLiveEntries([]);
      setLiveTotal(0);
    } finally {
      setLoading(false);
    }
  }, [isLive, categoryFilter, fromDate, toDate, search, page]);

  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // Demo/tour mode: read from localStorage and filter client-side
  const localEntries = useMemo(() => (isLive ? [] : getAuditLog()), [isLive]);

  const filtered = useMemo(() => {
    if (isLive) return liveEntries;

    let rows = localEntries;

    if (categoryFilter !== 'all') {
      rows = rows.filter((e) => e.category === categoryFilter);
    }
    if (fromDate) {
      const from = new Date(fromDate).getTime();
      rows = rows.filter((e) => new Date(e.timestamp).getTime() >= from);
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      rows = rows.filter((e) => new Date(e.timestamp).getTime() <= to.getTime());
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (e) =>
          e.changedBy.toLowerCase().includes(q) ||
          e.action.toLowerCase().includes(q) ||
          e.entityName.toLowerCase().includes(q) ||
          (e.detail ?? '').toLowerCase().includes(q) ||
          (e.oldValue ?? '').toLowerCase().includes(q) ||
          (e.newValue ?? '').toLowerCase().includes(q)
      );
    }

    return rows;
  }, [isLive, liveEntries, localEntries, categoryFilter, fromDate, toDate, search]);

  const allEntries = isLive ? liveEntries : localEntries;
  const totalFiltered = isLive ? liveTotal : filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const paginated = isLive ? filtered : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCategoryChange = (val: string) => {
    setCategoryFilter(val as CategoryFilter);
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
    setPage(1);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
    setPage(1);
  };

  const handleClearLog = () => {
    clearAuditLog();
    window.location.reload();
  };

  // ── Export to Excel ────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      if (isLive) {
        const allExportEntries = await fetchAllAuditLogEntriesFromApi({
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          search: search.trim() || undefined,
        });
        await exportAuditLogToExcel(allExportEntries);
      } else {
        await exportAuditLogToExcel(filtered);
      }
      toast.success('Export complete — file downloaded.');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Audit Log"
        subtitle="Complete history of all system changes across settings, projects, resources and budgets"
        size={20}
      />

      {/* Filters */}
      <Card data-tour="audit-log-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* Category */}
            <Select value={categoryFilter} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* From date */}
            <Input
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              className="w-44"
              placeholder="From date"
            />

            {/* To date */}
            <Input
              type="date"
              value={toDate}
              onChange={handleToDateChange}
              className="w-44"
              placeholder="To date"
            />

            {/* Free text search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by user, entity, action…"
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card data-tour="audit-log-table">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Audit Entries
            {totalFiltered > 0 && (
              <Badge variant="secondary" className="ml-1">
                {totalFiltered}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={filtered.length === 0 || exporting}
            className="h-9 w-9 p-0"
            aria-label="Export to Excel"
            title="Export to Excel"
            data-tour="audit-log-export"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-muted-foreground font-medium">Loading audit entries...</p>
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Shield className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground font-medium">No audit entries found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {allEntries.length === 0
                  ? 'Changes made in this session will appear here automatically.'
                  : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">Timestamp</TableHead>
                    <TableHead className="w-36">Changed By</TableHead>
                    <TableHead className="w-28">Category</TableHead>
                    <TableHead className="w-24">Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((entry: AuditEntry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(entry.timestamp)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {entry.changedBy}
                      </TableCell>
                      <TableCell>
                        <Badge variant={CATEGORY_BADGE_VARIANT[entry.category]} className="items-center">
                          {CATEGORY_ICON[entry.category] ?? null}
                          {entry.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{entry.action}</TableCell>
                      <TableCell className="text-sm font-medium">{entry.entityName}</TableCell>
                      <TableCell className="text-sm">
                        {entry.oldValue || entry.newValue ? (
                          <span className="flex items-center gap-1.5 flex-wrap">
                            {entry.oldValue && (
                              <span className="text-muted-foreground">{entry.oldValue}</span>
                            )}
                            {entry.oldValue && entry.newValue && (
                              <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                            )}
                            {entry.newValue && (
                              <span className="font-medium">{entry.newValue}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {entry.detail ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalFiltered)} of {totalFiltered}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Admin utility — only shown in demo mode when there are entries */}
      {!isLive && allEntries.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleClearLog}
          >
            Clear Audit Log
          </Button>
        </div>
      )}
    </div>
  );
}
